import { useState } from "react";
import { useBranding } from "@/shared/contexts/BrandingContext";
import { useAuth } from "@/shared/hooks/useAuth";
import { Button } from "@/shared/components/ui/button";
import OfflineIndicator from "@/components/offline/OfflineIndicator";
import SimplifiedNav from "@/shared/components/branding/greenshield/SimplifiedNav";
import AppNavigation from "@/components/navigation/AppNavigation";
import QuoteRequestForm from "@/components/modals/QuoteRequestForm";
import BookingSystem from "@/components/modals/BookingSystem";
import PaymentBookingForm from "@/core/components/PaymentBookingForm";

// New modular components
import BrandContactBar from "./header/BrandContactBar";
import HeaderLogo from "./header/HeaderLogo";
import MainNavigation from "./header/MainNavigation";
import MobileMenu from "./header/MobileMenu";
import HeaderAuth from "./header/HeaderAuth";

export default function Header() {
  const { user } = useAuth();
  const { isAegis360, isGreenshield } = useBranding();
  const [isOpen, setIsOpen] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [showPaymentBooking, setShowPaymentBooking] = useState(false);

  // Utility function to preserve brand parameter during navigation
  const preserveBrandUrl = (href: string) => {
    const currentParams = new URLSearchParams(window.location.search);
    const brandParam = currentParams.get("brand");

    if (brandParam) {
      try {
        const url = new URL(href, window.location.origin);
        url.searchParams.set("brand", brandParam);
        return url.pathname + url.search;
      } catch {
        // Fallback for relative URLs
        const separator = href.includes("?") ? "&" : "?";
        return `${href}${separator}brand=${encodeURIComponent(brandParam)}`;
      }
    }

    return href;
  };

  return (
    <>
      <OfflineIndicator />

      {/* Brand-specific Top contact bar */}
      <BrandContactBar onQuoteClick={() => setShowQuoteForm(true)} />

      {/* Main header */}
      <header className="bg-white/95 backdrop-blur-lg shadow-soft border-b border-emerald-100 sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Brand-specific Logo */}
            <div className="flex items-center">
              <HeaderLogo />
            </div>

            {/* Desktop Navigation - Choose between authenticated and public navigation */}
            {user ? (
              <AppNavigation />
            ) : isGreenshield ? (
              <SimplifiedNav />
            ) : (
              <MainNavigation preserveBrandUrl={preserveBrandUrl} />
            )}

            {/* Mobile Menu */}
            <MobileMenu
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              preserveBrandUrl={preserveBrandUrl}
            />

            {/* Quick Action Buttons & Auth (Desktop) */}
            <div className="hidden lg:flex items-center space-x-3">
              {isGreenshield && !user && (
                <>
                  <Button
                    onClick={() => setShowBooking(true)}
                    className="min-w-[100px]"
                    data-testid="button-book-now"
                  >
                    Book Now
                  </Button>
                  <Button
                    onClick={() => setShowQuoteForm(true)}
                    variant="outline"
                    className="min-w-[100px]"
                    data-testid="button-get-quote"
                  >
                    Get Quote
                  </Button>
                </>
              )}

              {/* Backend Access Button */}
              <Button
                onClick={() => (window.location.href = "/backend-admin")}
                variant="outline"
                leftIcon={
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                }
                data-testid="button-backend-access"
              >
                Admin
              </Button>

              <HeaderAuth />
            </div>
          </div>
        </div>
      </header>

      {/* Modals - Using simplified approach for now */}
      {showQuoteForm && (
        <QuoteRequestForm onClose={() => setShowQuoteForm(false)} />
      )}

      {showBooking && <BookingSystem onClose={() => setShowBooking(false)} />}

      {showPaymentBooking && (
        <PaymentBookingForm onClose={() => setShowPaymentBooking(false)} />
      )}
    </>
  );
}
